/**
 * Smart Asset Schema Initialization
 *
 * Dev-only loader for SmartAssetPrimitives schemas.
 * Replaces markdown-based template loading for local development.
 *
 * This module should be imported early in main.tsx to ensure schemas
 * are available before any components render.
 */

import {
  registerSchema,
  clearSchemas,
  getSchemaIds,
  parseJsonc,
  registerSmartAssetDefinition,
  clearSmartAssetDefinitions,
  bytesToHex,
  sha256,
} from "@satchel/core";
import type {
  ValueFlowsSchema,
  SmartAssetManifest,
  SmartAssetDefinitionDetails,
  SmartAssetContractProvenance,
  SmartAssetVerificationSummary,
  VerificationState,
} from "@satchel/core";

type LoaderLogger = Pick<Console, "log" | "warn">;

interface PrimitiveSchemaLoaderOptions {
  manifestModules: Record<string, string>;
  schemaModules: Record<string, string>;
  isDev: boolean;
  logger?: LoaderLogger;
}

const devManifestModules: Record<string, string> = import.meta.env.DEV
  ? (import.meta.glob(
      "../../../../../SmartAssetPrimitives/smart-asset-*/smart-asset-structure/smartasset.jsonc",
      {
        query: "?raw",
        import: "default",
        eager: true,
      },
    ) as Record<string, string>)
  : {};

const devSchemaModules: Record<string, string> = import.meta.env.DEV
  ? (import.meta.glob(
      "../../../../../SmartAssetPrimitives/smart-asset-*/smart-asset-structure/schema/value-flows.jsonc",
      { query: "?raw", import: "default", eager: true },
    ) as Record<string, string>)
  : {};

const DEFAULT_DEFINITION_REPO_BASE = "git+ssh://gitlab.com/smart-assets.io/smart-asset-primitives";
const DEFINITION_REPO_BASE = (
  import.meta.env.VITE_SMART_ASSET_REPO_BASE || DEFAULT_DEFINITION_REPO_BASE
).replace(/\/$/, "");

function extractPrimitiveRoot(path: string): string | null {
  const match = path.match(/smart-asset-[^/]+/);
  return match ? match[0] : null;
}

function shouldSkip(path: string): boolean {
  return !extractPrimitiveRoot(path);
}

function toKebabCase(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

function resolveAssetId(manifest: SmartAssetManifest, root: string): string {
  const name = manifest.name?.trim();
  if (name) {
    return toKebabCase(name);
  }
  return root.replace(/^smart-asset-/, "");
}

function hashContent(content: string): string {
  const bytes = new TextEncoder().encode(content);
  return `sha256:${bytesToHex(sha256(bytes))}`;
}

function normalizeVerificationOverall(
  summary: SmartAssetVerificationSummary,
): "pending" | "verified" | "mismatch" {
  const checks = [
    summary.definition.manifest,
    summary.definition.schema,
    summary.definition.signature,
    summary.deployment,
  ];
  if (checks.includes("mismatch")) {
    return "mismatch";
  }
  if (checks.every((check) => check === "match")) {
    return "verified";
  }
  return "pending";
}

function buildDefinitionUri(root: string): string {
  return `${DEFINITION_REPO_BASE}/${root}.git`;
}

function buildMockContracts(assetId: string, schemaHash: string): SmartAssetContractProvenance[] {
  return [
    {
      chain: "RGB",
      contractId: `RGB:mock-${assetId}`,
      status: "mocked",
      artifactHash: schemaHash,
      hashStatus: "pending",
      verified: false,
      notes: "Deployment pipeline pending (mocked provenance).",
    },
  ];
}

function buildDefinitionDetails(
  manifest: SmartAssetManifest,
  manifestContent: string,
  schemaContent: string,
  root: string,
  assetId: string,
): SmartAssetDefinitionDetails {
  const manifestHash = hashContent(manifestContent);
  const schemaHash = hashContent(schemaContent);
  const sourceCommitHash = manifest.source_commit_hash?.trim() || undefined;
  const signatureStatus: VerificationState = "missing";
  const verification: SmartAssetVerificationSummary = {
    overall: "pending",
    definition: {
      manifest: "match",
      schema: "match",
      signature: signatureStatus,
    },
    deployment: "pending",
    notes: "Contract deployment verification is mocked until the pipeline ships.",
  };
  verification.overall = normalizeVerificationOverall(verification);

  return {
    manifest,
    source: {
      uri: buildDefinitionUri(root),
      ref:
        sourceCommitHash ??
        (manifest.version.startsWith("v") ? manifest.version : `v${manifest.version}`),
      sourceCommitHash,
      manifestHash,
      schemaHash,
      signature: {
        status: signatureStatus,
      },
      importedAt: new Date().toISOString(),
      localPath: root,
    },
    contracts: buildMockContracts(assetId, schemaHash),
    verification,
  };
}

export function registerPrimitiveSchemas({
  manifestModules,
  schemaModules,
  isDev,
  logger = console,
}: PrimitiveSchemaLoaderOptions): number {
  if (!isDev) {
    logger.warn("[SATCHEL] Smart Asset schema loader disabled outside dev.");
    return 0;
  }

  const schemaByRoot = new Map<string, string>();
  for (const [path, content] of Object.entries(schemaModules)) {
    if (shouldSkip(path)) {
      continue;
    }
    const root = extractPrimitiveRoot(path);
    if (root) {
      schemaByRoot.set(root, content);
    }
  }

  let loadedCount = 0;

  for (const [path, content] of Object.entries(manifestModules)) {
    if (shouldSkip(path)) {
      continue;
    }

    const root = extractPrimitiveRoot(path);
    if (!root) {
      continue;
    }

    const schemaContent = schemaByRoot.get(root);
    if (!schemaContent) {
      logger.warn(`[SATCHEL] No schema found for ${root}`);
      continue;
    }

    try {
      const manifest = parseJsonc<SmartAssetManifest>(content);
      const schema = parseJsonc<ValueFlowsSchema>(schemaContent);
      const assetId = resolveAssetId(manifest, root);
      registerSchema(assetId, schema);
      const definition = buildDefinitionDetails(manifest, content, schemaContent, root, assetId);
      registerSmartAssetDefinition(assetId, definition);
      loadedCount++;
    } catch (error) {
      logger.warn(`[SATCHEL] Failed to load schema for ${root}:`, error);
    }
  }

  if (loadedCount > 0) {
    logger.log(`[SATCHEL] Loaded ${loadedCount} Smart Asset schema(s) from primitives`);
    logger.log("[SATCHEL] Registered schema IDs:", getSchemaIds());
  } else {
    logger.warn("[SATCHEL] No Smart Asset schemas loaded from primitives");
  }

  return loadedCount;
}

/**
 * Initialize Smart Asset schemas from SmartAssetPrimitives (dev-only).
 *
 * @returns Number of schemas successfully loaded
 */
export function initializeSchemas(): number {
  clearSchemas();
  clearSmartAssetDefinitions();

  return registerPrimitiveSchemas({
    manifestModules: devManifestModules,
    schemaModules: devSchemaModules,
    isDev: import.meta.env.DEV,
  });
}

// Auto-initialize on import
const schemasLoaded = initializeSchemas();

export { schemasLoaded };
